/**
 * The macro view: every person, what is on them, and what is heading their way.
 *
 * Deliberately sparse. Client › Project › Deliverable › Task, a date, and
 * nothing else. The job is to answer "who is underwater" in one glance and then
 * get out of the way — a row carrying every field it could carry becomes a
 * report people stop opening.
 *
 * The hours bar under each person is the second question: not just how many
 * things, but how much work. In-process and future are drawn as one bar because
 * what matters is the total against the window, and two separate numbers make
 * you do the addition yourself.
 *
 * **Since E8 the bar is drawn against CAPACITY**, not against the busiest
 * person: hours per day (a setting) x the business days in the window, so a
 * full bar means "full" and over 100% means over. Until then the scale was a
 * display device -- the busiest person drew full -- and said nothing about
 * whether anybody could take more.
 *
 * **Unestimated work is not drawn as room.** A person whose stages nobody
 * estimated would draw EMPTY on a capacity bar, reading as "has room" when it
 * means "unknown" -- recommending the person nobody planned for. Their empty
 * remainder is hatched instead, and the line says the bar may be low.
 *
 * **Each row carries its own hours, and the two parts are labelled** (punch
 * list F135/F148, 30 Oct 2026). Testers could not tell how the total was made
 * up, nor which work was in hand: the only cue was a fainter row. Display
 * only -- the rows and the bar are what they were.
 *
 * **Everybody is listed** (F132/F134): the server sends every active person,
 * so "Include people with nothing" now has somebody to include.
 */
import type { Workload as WorkloadData, WorkloadPerson, WorkloadRow } from "@fieldpath/shared";
import { mdy, mdyWeekday } from "@fieldpath/shared";
import { useEffect, useState } from "react";

import { api, type Fetcher } from "../api/client";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { HelpButton } from "./HelpButton";
import { KindTag } from "./KindTag";
import { useTaskHover } from "./TaskHoverCard";
import type { TaskHoverBind } from "./TaskHoverCard";

interface Props {
  fetcher: Fetcher;
  onOpenTask: (taskId: string) => void;
}

/**
 * Offered windows, ALL in WORKING days (punch list #13, 12 Oct 2026, and
 * Jeremy's yes to the rest the same day): capacity is counted in business
 * days, so a calendar window held an awkward number of them. 1 is today when
 * today is a working day.
 */
interface WindowChoice {
  readonly n: number;
  readonly working: boolean;
}
const WINDOWS: readonly WindowChoice[] = [1, 3, 5, 10, 30].map((n) => ({ n, working: true }));

export function Workload({ fetcher, onOpenTask }: Props) {
  // Asked once for the whole screen, so the person header can mark which of
  // these people is you. An existing endpoint and one call per page load.
  const [meKey, setMeKey] = useState<string | null>(null);
  // One hook per screen: `bind` goes on each row, `card` renders the portal.
  const { bind, card } = useTaskHover(fetcher);

  useEffect(() => {
    void api
      .me(fetcher)
      .then((r) => setMeKey(r.employee?.ajeraKey ?? null))
      // Not knowing who you are hides a highlight. It is not a reason to fail
      // a screen that works perfectly well without one.
      .catch(() => setMeKey(null));
  }, [fetcher]);
  // 5 working days by default -- it replaced 7 calendar days, the old default.
  const [choice, setChoice] = useState<WindowChoice>(WINDOWS[2]!);
  // The window as the server resolved it: calendar days ahead, and the last
  // day by name when it was counted in working days.
  const [span, setSpan] = useState<{ days: number; through: string | null }>({ days: 7, through: null });
  const [data, setData] = useState<WorkloadData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyOnly, setBusyOnly] = useState(true);
  // Work with no due date (F181, Jeremy 1 Nov 2026): listed whatever the
  // window, and this hides it. Ticked by default. What is listed only -- the
  // bars are the server's and do not change.
  const [undated, setUndated] = useState(true);
  const [find, setFind] = useState("");

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(() => {
    let current = true;
    // A working-day window is resolved by the server first, holidays and
    // all, then asked for as calendar days like any other window.
    const resolve: Promise<{ days: number; through: string | null }> = choice.working
      ? api.businessWindow(fetcher, choice.n).then((w) => ({ days: w.calendarDays, through: w.through }))
      : Promise.resolve({ days: choice.n, through: null });
    resolve
      .then((s) =>
        api.workload(fetcher, s.days).then((d) => {
          if (current) {
            setSpan(s);
            setData(d);
            setError(null);
          }
        }),
      )
      .catch((e: Error) => { if (current) setError(e.message); });
    return () => { current = false; };
  }, [fetcher, choice, back]);
  const days = span.days;

  if (error) return <div className="error">{error}</div>;
  if (!data) return <p className="muted">Loading…</p>;

  const needle = find.trim().toLowerCase();
  const dated = (r: WorkloadRow) => undated || r.stageDueDate !== null;
  const people = data.people
    .map((p) => (undated ? p : { ...p, current: p.current.filter(dated), upcoming: p.upcoming.filter(dated) }))
    .filter((p) => (busyOnly ? p.current.length > 0 || p.upcoming.length > 0 : true))
    .filter((p) => (needle === "" ? true : p.name.toLowerCase().includes(needle)));

  // One capacity for everybody until PTO is subtracted per person (E8.6).
  const capacity = data.capacity;

  const late = data.people.reduce((n, p) => n + p.lateCount, 0);
  const anyWork = data.people.some((p) => p.current.length > 0 || p.upcoming.length > 0);

  return (
    <>
      {card}
      <div className="count-line">
        {people.length} {people.length === 1 ? "PERSON" : "PEOPLE"}
        {late > 0 ? ` · ${late} LATE` : ""}
      </div>
      <h1 className="page-h1">
        Workload
        <HelpButton screens="workload" />
      </h1>

      {/* What a full bar means, said once above all of them. */}
      <p className="muted load-capacity">
        A full bar is {capacity.hours} h: {capacity.businessDays} working day
        {capacity.businessDays === 1 ? "" : "s"} at {capacity.hoursPerDay} h,{" "}
        {days === 0
          ? "today only"
          : `today through ${span.through ? mdyWeekday(span.through) : `${days} days out`}`}
        .
        {capacity.holidaysKnown
          ? ""
          : " The holiday list does not cover all of it, so those days count as working and the figure may be high."}
        {data.timeOff.available ? " Each person's time off comes out of their own bar." : ""}
      </p>
      {/* SAID, in the warning style, because the failure is invisible
          otherwise: every bar would quietly assume nobody is away. */}
      {data.timeOff.available ? null : (
        <p className="admin-warn">
          Time off could not be read from the scheduler, so every bar assumes nobody is away.{" "}
          <span className="muted">{data.timeOff.reason}</span>
        </p>
      )}

      <div className="row" style={{ marginBottom: 16, gap: 10 }}>
        <span className="muted">Working days ahead</span>
        <div className="viewpick" role="group" aria-label="Window">
          {WINDOWS.map((w) => (
            <button
              key={`${w.n}${w.working ? "w" : "c"}`}
              className={`seg${w === choice ? " on" : ""}`}
              aria-pressed={w === choice}
              onClick={() => setChoice(w)}
            >
              {w.n}
            </button>
          ))}
        </div>
        <label className="row" style={{ gap: 6 }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={!busyOnly}
            onChange={(e) => setBusyOnly(!e.target.checked)}
          />
          <span className="muted">Include people with nothing</span>
        </label>
        <label className="row" style={{ gap: 6 }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={undated}
            onChange={(e) => setUndated(e.target.checked)}
          />
          <span className="muted">Include work with no due date</span>
        </label>
        <input
          type="search"
          value={find}
          placeholder="Find a person…"
          aria-label="Find a person"
          onChange={(e) => setFind(e.target.value)}
          style={{ width: "auto", minWidth: 170 }}
        />
      </div>

      {people.length === 0 ? (
        <div className="empty">
          {!anyWork && busyOnly && needle === ""
            ? "Nothing is assigned to anybody yet."
            : "Nobody matches that."}
        </div>
      ) : (
        people.map((person) => (
          <Person
            key={person.employeeKey}
            person={person}
            capacity={person.capacityHours}
            days={days}
            onOpenTask={onOpenTask}
            hover={bind}
            isMe={person.employeeKey === meKey}
          />
        ))
      )}
    </>
  );
}

function Person({
  person,
  capacity,
  days,
  onOpenTask,
  hover,
  isMe,
}: {
  person: WorkloadPerson;
  /** Hours one person can carry in the window: 100% of the bar. */
  capacity: number;
  days: number;
  onOpenTask: (taskId: string) => void;
  // Threaded one level further than on the other screens, because the rows
  // here live inside a Person rather than beside the hook.
  hover: TaskHoverBind;
  /** Matched on the Ajera KEY, never the display name. */
  isMe: boolean;
}) {
  const inProcess = person.inProcessHours ?? 0;
  const future = person.futureHours ?? 0;
  const unestimated = person.unestimatedCurrent + person.unestimatedUpcoming;
  const total = inProcess + future;
  // A window with no working days in it (a weekend look-ahead) carries no
  // capacity; anything at all on it is over.
  const pct = capacity > 0 ? Math.round((total / capacity) * 100) : total > 0 ? Infinity : 0;
  const share = (h: number) => (capacity > 0 ? Math.min(100, (h / capacity) * 100) : h > 0 ? 100 : 0);
  const nowWidth = share(inProcess);
  const nextWidth = Math.min(100 - nowWidth, share(future));

  return (
    <section className="load">
      <div className="load-head">
        <h3 className="load-name">
          <span className="avatar avatar-lg" aria-hidden="true">
            {initials(person.name)}
          </span>
          {/* Your own row, on a screen that lists everybody. The pulse is the
              cue; YOU is the word, for anyone who cannot see it pulse. */}
          <span className={isMe ? "mine-name" : undefined}>{person.name}</span>
          {isMe ? <span className="you-label">YOU</span> : null}
        </h3>
        <span className="load-counts">
          {person.current.length} with them
          {/* A badge rather than bold red text. "3 late" set in the same
              sentence as "9 with them" is read as part of the count; as a
              badge it is read as a problem. */}
          {person.lateCount > 0 ? (
            <span className="late-badge">{person.lateCount} LATE</span>
          ) : null}
          {person.upcoming.length > 0 ? ` · ${person.upcoming.length} coming` : ""}
        </span>
      </div>

      {/* One bar, two segments, against the window's capacity. Over capacity
          the bar is full and edged in the late colour, and the percentage
          says by how much. */}
      <div
        className={`load-bar${pct > 100 ? " over" : ""}${unestimated > 0 ? " unknown" : ""}`}
        title={`${inProcess} h in process, ${future} h arriving, against ${capacity} h`}
      >
        <div className="seg-now" style={{ width: `${nowWidth}%` }} />
        <div className="seg-next" style={{ width: `${nextWidth}%` }} />
      </div>
      <div className="load-hours">
        <span><i className="seg-now" />{fmtHours(person.inProcessHours)} in process</span>
        <span><i className="seg-next" />{fmtHours(person.futureHours)} arriving</span>
        {person.hoursOff !== null && person.hoursOff > 0 ? (
          <span className="load-off">{fmtHours(person.hoursOff)} off</span>
        ) : null}
        <span className={pct > 100 ? "load-pct over" : "load-pct"}>
          {Number.isFinite(pct) ? `${pct}%` : "over"} of {capacity} h
        </span>
        {/* Said out loud, because a total built from half the rows is a lie
            unless it admits which half. Null means nobody estimated — it never
            means zero. */}
        {unestimated > 0 ? (
          <span className="muted">{unestimated} not estimated, so the bar may be low</span>
        ) : null}
      </div>

      {person.current.length === 0 && person.upcoming.length === 0 ? (
        <p className="muted load-nothing">Nothing assigned in this window.</p>
      ) : null}
      {/* Labelled, because a fainter row was the only thing telling work in
          hand from work on its way (F135). */}
      {person.current.length > 0 ? <div className="load-part">In progress</div> : null}
      {person.current.map((row) => (
        <Line
          key={`${row.taskId}-${row.stageName}`}
          row={row}
          onOpen={onOpenTask}
          hover={hover}
        />
      ))}
      {person.upcoming.length > 0 ? <div className="load-part">Coming up</div> : null}
      {person.upcoming.map((row) => (
        <Line
          key={`u-${row.taskId}-${row.stageName}`}
          row={row}
          onOpen={onOpenTask}
          hover={hover}
          coming
        />
      ))}
    </section>
  );
}

function Line({
  row,
  onOpen,
  coming,
  hover,
}: {
  row: WorkloadRow;
  onOpen: (taskId: string) => void;
  coming?: boolean;
  hover: TaskHoverBind;
}) {
  const late = !coming && row.daysUntilDue !== null && row.daysUntilDue < 0;
  return (
    <button
      className={`load-row${late ? " overdue" : ""}${coming ? " coming" : ""}`}
      onClick={() => onOpen(row.taskId)}
      {...hover(row.taskId, row.taskTitle)}
    >
      <span className="load-where">
        {row.clientName ? (
          <>
            <span className="client-name">{row.clientName}</span>
            <span className="sep"> › </span>
          </>
        ) : null}
        <KindTag kind="project" size="crumb" />
        {row.projectName}
        {row.deliverableName ? (
          <>
            <span className="sep"> › </span>
            <KindTag kind="deliverable" size="crumb" />
            <span className="deliv-name">{row.deliverableName}</span>
          </>
        ) : null}
      </span>
      <span className="load-task task-name">
        <KindTag kind="task" size="row" />
        {row.taskTitle}
        <span className="muted"> · {row.stageName}</span>
        {row.isBlocked ? <span className="blocked-badge">BLOCKED</span> : null}
      </span>
      {/* The row's own share of the bar (F135). A dash, never "0 h", when
          nobody estimated it. */}
      <span
        className="load-row-hours"
        title={row.allocatedHours === null ? "Not estimated" : "Hours allocated to this stage"}
      >
        {fmtHours(row.allocatedHours)}
      </span>
      <span className="load-when">{when(row, coming)}</span>
    </button>
  );
}

/**
 * For work in hand, when it is due. For work coming, when it should ARRIVE —
 * a due date they cannot act on yet is not the thing they need to know.
 */
function when(row: WorkloadRow, coming?: boolean): string {
  if (coming) {
    // Jeremy's words (F181, 1 Nov 2026): "possible" only where the work might
    // never reach the stage; the stage's own date after when it arrives.
    const arrives = row.expectedArrival ? `arrives after ${mdy(row.expectedArrival)}` : "arrival unknown";
    const due = row.stageDueDate ? `due ${mdy(row.stageDueDate)}` : "no due date set";
    return `${row.possible ? "possible, " : ""}${arrives}, ${due}`;
  }
  if (row.stageDueDate === null) return "no date";
  const d = row.daysUntilDue;
  if (d === null) return mdy(row.stageDueDate);
  if (d < 0) return `${Math.abs(d)} day${Math.abs(d) === 1 ? "" : "s"} late`;
  if (d === 0) return "due today";
  if (d === 1) return "due tomorrow";
  if (d <= 5) return `due in ${d} days`;
  return mdy(row.stageDueDate);
}

/** Null is "nobody estimated", and must never print as 0 h. */
function fmtHours(value: number | null): string {
  return value === null ? "—" : `${Number.isInteger(value) ? value : value.toFixed(1)} h`;
}

/** The first letters of a name, for the round avatar. */
function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  return (words[0]![0]! + (words[1]?.[0] ?? "")).toUpperCase();
}
